import React, { useMemo } from "react";
import ReactTable from "@dhn/components/molecules/data-table";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import SectionHeader from "@dhn/components/molecules/section-header";
import CatalogSelect from "./CatalogSelect";
import { formatFileSize } from "./document-utils";
import StatusBadge from "@dhn/components/molecules/status-badge";

const ACCEPTED_DOCUMENT_TYPES =
  ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";

export default function SpecialityEvidence({
  specialities = [],
  specialityOptions = [],
  documents = [],
  isSubmitting,
  isLoadingSpecialities,
  onAddSpeciality,
  onRemoveSpeciality,
  removeSpecialityEvidenceById,
  onDraftStateChange,
  onFilesChange,
  openDocumentsModal,
  inlineErrors = {},
}) {
  const [draftId, setDraftId] = React.useState(null);
  const availableOptions = specialityOptions.filter(
    (option) => !specialities.some((item) => Number(item.id) === Number(option.id)),
  );

  const setDraft = (value) => {
    setDraftId(value);
    onDraftStateChange(Boolean(value));
  };

  const addSpeciality = () => {
    if (!draftId) return;
      onAddSpeciality(draftId);
    setDraftId(null);
    onDraftStateChange(false);
  };

  const tableData = useMemo(
    () =>
      specialities.map((speciality) => {
        const specialityOption = specialityOptions.find(
          (option) => Number(option.id) === Number(speciality.id),
        );

        return {
          ...speciality,
          name: speciality.name || specialityOption?.name || "",
          documents: documents.filter(
            (document) => Number(document.specialityId) === Number(speciality.id),
          ),
        };
      }),
    [documents, specialityOptions, specialities],
  );

  const columns = useMemo(
    () => [
      {
        Header: "Especialidad",
        accessor: "name",
      },
        {
          Header: "Archivo",
          Cell: ({ original }) =>
            original.documents.length ? (
              <div style={{ minWidth: 0 }}>
                {original.documents.map((document) => (
                  <div key={document.id || document.name} className="text-truncate">
                    <i className="fa fa-file-text-o text-info mr-2" />
                    {document.name}
                    <small className="text-muted ml-2">
                      {formatFileSize(document.size)}
                    </small>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-muted">
                <i className="fa fa-paperclip mr-1" />
                Sin archivo adjunto
              </span>
            ),
        },
        {
          Header: "Estado",
          width: 120,
          Cell: ({ original }) =>
            original.documents.length ? (
              <StatusBadge color="success" icon="fa-check">
                Completo
              </StatusBadge>
            ) : (
              <StatusBadge color="warning" icon="fa-exclamation-circle">
                Pendiente
              </StatusBadge>
            ),
        },
        {
          Header: "Acciones",
          width: 300,
          Cell: ({ original }) => {
            const inputId = `speciality-evidence-${original.id}`;

            return (
              <div className="d-flex align-items-center flex-wrap">
                <Label
                  for={inputId}
                  className="btn btn-primary btn-sm mb-0 mr-2"
                >
                  <i className="fa fa-upload mr-1" />
                  {original.documents.length ? "Reemplazar" : "Adjuntar"}
                </Label>
                <Input
                  id={inputId}
                  type="file"
                  multiple
                  accept={ACCEPTED_DOCUMENT_TYPES}
                  className="d-none"
                  disabled={isSubmitting}
                  onChange={(event) =>
                    onFilesChange(event, {
                      requirementKey: "specialtyDegreeMsp",
                      specialityId: original.id,
                    })
                  }
                />
                {original.documents.map((document) => (
                  <React.Fragment key={document.id || document.name}>
                    <Button
                      color="secondary"
                      type="button"
                      size="sm"
                      className="btn-color-gray mr-1"
                      title="Ver documento"
                      disabled={isSubmitting}
                      onClick={() => openDocumentsModal(document, "speciality")}
                    >
                      <i className="fa fa-eye" />
                      <span className="ml-1">Ver</span>
                    </Button>
                  </React.Fragment>
                ))}
                <Button
                  type="button"
                  color="danger"
                  className="btn-color-danger"
                  size="sm"
                  title="Borrar especialidad"
                  disabled={isSubmitting}
                  onClick={() => {
                    removeSpecialityEvidenceById(original.id);
                    onRemoveSpeciality(
                      specialities.findIndex((speciality) => speciality.id === original.id),
                    );
                  }}
                >
                  <i className="fa fa-trash mr-1" />
                  Borrar
                </Button>
              </div>
            );
          },
        },
      ],
      [
        isSubmitting,
        onFilesChange,
        onRemoveSpeciality,
        openDocumentsModal,
        removeSpecialityEvidenceById,
        specialities,
      ],
    );

    return (
      <Card className="fade-in-up mb-3">
        <CardBody>
          <SectionHeader
            icon="fa-graduation-cap"
            title={<>Especialidades <span className="required-mark">*</span></>}
            description="Agregue una evidencia por cada especialidad seleccionada."
            actions={
              <StatusBadge
                color={tableData.length ? "success" : "warning"}
              >
                {tableData.length} seleccionada{tableData.length === 1 ? "" : "s"}
              </StatusBadge>
            }
          />
          <Row form className="align-items-end mb-3">
            <Col xs={12} md={3}>
              <CatalogSelect
                inputId="specialityDraftId"
                options={availableOptions}
                value={draftId}
                invalid={Boolean(inlineErrors.specialities)}
                loading={isLoadingSpecialities}
                disabled={isSubmitting || isLoadingSpecialities}
                placeholder="Seleccione una especialidad"
                onChange={setDraft}
              />
            </Col>
            <Col xs="auto" className="mt-2 mt-md-0">
              <Button
                type="button"
                color="primary"
                className="btn-color-primary"
                disabled={isSubmitting || isLoadingSpecialities || !draftId}
                onClick={addSpeciality}
              >
                <i className="fa fa-plus mr-1" />
                Agregar
              </Button>
            </Col>
          </Row>

          {inlineErrors.specialities && (
            <small className="text-danger d-block mb-2">
              {inlineErrors.specialities}
            </small>
          )}

          <ReactTable
            key={tableData.map((speciality) => speciality.id).join("-") || "empty"}
            data={tableData}
            columns={columns}
            pageSize={Math.max(tableData.length, 1)}
            minRows={0}
            showPagination={false}
            noDataText="Agregue al menos una especialidad"
            className="-striped -highlight rounded"
          />
        </CardBody>
      </Card>
    );
}
